import React, { useEffect, useState, memo } from "react";
import { Card, Spin, message, Space, Segmented, DatePicker, Button, Skeleton } from "antd";
import { MetricCardHeader } from "./Shared/MetricCardHeader";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import { getActivityTrend } from "../../api/api";
import type { ActivityDataPoint } from "../../types/api-models";
import { 
  formatXAxisLabel, 
  formatTooltipLabel, 
  formatPeriodDisplay,
  getXAxisInterval,
  type AggregationType 
} from "../../utils/dateFormatters";

const { RangePicker } = DatePicker;

interface ActivityTrendProps {
  defaultAggregation?: AggregationType;
  isExpanded?: boolean;
  dashboardData?: any;
}

const ActivityTrendChart: React.FC<ActivityTrendProps> = ({
  defaultAggregation = 'daily',
  isExpanded = false,
  dashboardData
}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [aggregation, setAggregation] = useState<AggregationType>(defaultAggregation);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let from_date: string | undefined;
      let to_date: string | undefined;

      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setLoading(false);
          return;
        }
        
        // Validate max 90 days
        const diffDays = dateRange[1].diff(dateRange[0], 'day');
        if (diffDays > 90) {
          message.error('Daily view limited to 90 days');
          setLoading(false);
          return;
        }
        
        from_date = dateRange[0].format('YYYY-MM-DD');
        to_date = dateRange[1].format('YYYY-MM-DD');
      }
      // Weekly and monthly don't need date range (backend uses fixed ranges)

      const response = await getActivityTrend(aggregation, from_date, to_date);
      // Transform API data to match chart expectations
      const transformedData = response.data_points.map((point: any) => ({
        date: point.period_start,
        sessions: point.total_sessions,
        questions: point.total_questions,
        messages: point.total_messages || (point.total_questions * 2) // Fallback if 0
      }));
      setData(transformedData);
    } catch (error) {
      message.error("Failed to load activity trend");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip initial fetch in expanded mode
    if (isExpanded) return;
    
    // For collapsed view, use dashboard data
    if (dashboardData?.activity_trend?.data_points) {
      const points = dashboardData.activity_trend.data_points;
      const transformed = points.map((point: any) => ({
        date: point.period_start,
        sessions: point.total_sessions,
        questions: point.total_questions,
        messages: point.total_messages || (point.total_questions * 2)
      }));
      setData(transformed);
    }
  }, [isExpanded, dashboardData]);

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px 0' }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  // Loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading activity trend..." />
      </div>
    );
  }


  // Calculate summary statistics (simple, no memoization needed for small datasets)
  const totalSessions = data.reduce((sum, item) => sum + (item.sessions || 0), 0);
  const totalQuestions = data.reduce((sum, item) => sum + (item.questions || 0), 0);
  const totalMessages = data.reduce((sum, item) => sum + (item.messages || 0), 0);
  const avgQuestionsPerSession = totalSessions > 0 ? (totalQuestions / totalSessions).toFixed(1) : 0;

  // Chart component
  const chartComponent = data.length > 0 ? (
    <>
      {/* Summary Statistics provided individually by MetricCardHeader for Total Sessions */}
      {!isExpanded && (
        <div className="mb-4">
           {/* Primary Metric: Total Sessions with Trend */}
           <MetricCardHeader 
              title="Total Sessions"
              value={totalSessions.toLocaleString()}
              trend={dashboardData?.activity_trend?.trend}
              subtitle={`${totalQuestions.toLocaleString()} Questions • ${avgQuestionsPerSession} Q/Session`}
           />
        </div>
      )}
      
      <div style={{ height: isExpanded ? 400 : 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 32, left: 8, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorQuestions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(val) => formatXAxisLabel(val, aggregation)}
              interval={getXAxisInterval(data.length)}
              angle={data.length > 30 ? -45 : 0}
              textAnchor={data.length > 30 ? 'end' : 'middle'}
              height={data.length > 30 ? 60 : 20}
              style={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip 
              labelFormatter={(val) => formatTooltipLabel(val, aggregation)}
              contentStyle={{ fontSize: 13 }}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="questions"
              name="Questions"
              stroke="#8884d8"
              fillOpacity={1}
              fill="url(#colorQuestions)"
              activeDot={{ r: 6, stroke: '#8884d8', strokeWidth: 2 }}
              dot={{ r: 4, fill: '#8884d8', stroke: '#fff', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="sessions"
              name="Sessions"
              stroke="#82ca9d"
              fillOpacity={1}
              fill="url(#colorSessions)"
              activeDot={{ r: 6, stroke: '#82ca9d', strokeWidth: 2 }}
              dot={{ r: 4, fill: '#82ca9d', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </>
  ) : (
    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
      No activity data available
    </div>
  );

  // If not expanded, return chart only
  if (!isExpanded) {
    return chartComponent;
  }

  // Standardized Expanded view
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
          <Segmented
            value={aggregation}
            onChange={(value) => setAggregation(value as AggregationType)}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' },
            ]}
          />
          {aggregation === 'daily' ? (
            <>
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: 260 }}
                disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
              />
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
              Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
            </span>
          )}
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={data.length === 0}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Chart */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Spin size="large" tip="Loading activity data..." />
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view activity trend</p>
          <p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
        </div>
      ) : (
        <div style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
            data={data}
            margin={{ top: 10, right: 32, left: 8, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorQuestions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(val) => formatXAxisLabel(val, aggregation)}
              interval={getXAxisInterval(data.length)}
              angle={data.length > 30 ? -45 : 0}
              textAnchor={data.length > 30 ? 'end' : 'middle'}
              height={data.length > 30 ? 60 : 20}
              style={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip 
              labelFormatter={(val) => formatTooltipLabel(val, aggregation)}
              contentStyle={{ fontSize: 13 }}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="questions"
              name="Questions"
              stroke="#8884d8"
              fillOpacity={1}
              fill="url(#colorQuestions)"
              activeDot={{ r: 6, stroke: '#8884d8', strokeWidth: 2 }}
              dot={{ r: 4, fill: '#8884d8', stroke: '#fff', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="sessions"
              name="Sessions"
              stroke="#82ca9d"
              fillOpacity={1}
              fill="url(#colorSessions)"
              activeDot={{ r: 6, stroke: '#82ca9d', strokeWidth: 2 }}
              dot={{ r: 4, fill: '#82ca9d', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const MemoizedActivityTrendChart = memo(ActivityTrendChart);
MemoizedActivityTrendChart.displayName = 'ActivityTrendChart';

export default MemoizedActivityTrendChart;
